/* =====================================================================
 * WorkspaceListSidebar styles
 *
 * Extracted from WorkspaceListSidebar.tsx so the presentation layer and
 * the (large) component logic can be edited independently. Behavior is
 * unchanged — all visual rules, selectors, and theme reads are 1:1.
 * ===================================================================== */

import styled, { css } from 'styled-components';
import {
  FileText,
  Folder,
  Image as ImageIcon,
  Presentation,
} from 'lucide-react';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(23,23,23,0.82)'
      : 'rgba(249,249,249,0.78)'};
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  padding: ${({ theme }) => theme.sidebar.headerPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.sidebarMobile.headerHeight};
    padding: ${({ theme }) => theme.sidebarMobile.headerPad};
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const MainSidebarOpenBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const SidebarToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.56)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,20,24,0.42)' : 'rgba(255,255,255,0.38)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 10px 24px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 10px 24px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.48)'};
  backdrop-filter: blur(18px) saturate(170%);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(48,51,58,0.68)' : 'rgba(255,255,255,0.52)'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.74)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

export const Title = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8125rem;
  }
`;

export const List = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.dm.listPad};

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.dmMobile.listPad};
  }
`;

export const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 8px 8px 4px;
    font-size: 0.6875rem;
  }
`;

export const SectionTitle = styled.span``;

export const SectionRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const InlineIconBtn = styled.button`
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const CreateMenuRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 4px 10px 8px;
`;

export const CreateMenuBtn = styled.button`
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const TreeWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 2px 4px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
`;

export const DropInsertLine = styled.div<{ $depth: number }>`
  position: relative;
  height: 0;
  margin: 2px 8px;
  margin-left: ${({ $depth }) => `${8 + $depth * 14}px`};
  border-top: 3px solid ${({ theme }) => theme.colors.warning};
  border-radius: 999px;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    left: -4px;
    top: -5px;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.warning};
  }
`;

export const TreeItem = styled.div<{
  $active: boolean;
  $depth: number;
  $dropActive: boolean;
  $dropDenied: boolean;
  $dragging: boolean;
}>`
  width: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  text-align: left;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: ${({ $depth }) => `6px 8px 6px ${8 + $depth * 14}px`};
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'pointer')};
  transition: background ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast},
    opacity ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : '#ffffff'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)'};
      font-weight: 600;
    `}

  ${({ $dropActive, theme }) =>
    $dropActive &&
    css`
      background: ${theme.colors.warningBg};
      box-shadow: inset 0 0 0 1px ${theme.colors.warning};
    `}

  ${({ $dropDenied, theme }) =>
    $dropDenied &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)'};
      box-shadow: inset 0 0 0 1px ${theme.colors.error};
    `}

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.55;
      transform: scale(0.995);
    `}
`;

export const ImageLeafItem = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  text-align: left;
  padding: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
`;

export const ImageLeafRow = styled.div<{ $active: boolean; $depth: number }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: ${({ $depth }) => `6px 8px 6px ${8 + $depth * 14}px`};
  min-height: 31px;
  transition: background ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : '#ffffff'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)'};

      ${ImageLeafItem} {
        font-weight: 600;
      }
    `}
`;

export const ImageLeafActions = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${ImageLeafRow}:hover &,
  ${ImageLeafRow}:focus-within & {
    opacity: 1;
  }
`;

export const ImageLeafSpacer = styled.span`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

export const TreeNodeMain = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const TreeToggle = styled.button`
  width: 16px;
  height: 16px;
  border: none;
  padding: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const TreeSpacer = styled.span`
  width: 13px;
  height: 13px;
  display: inline-block;
`;

export const FolderIcon = styled(Folder)`
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${TreeItem}:hover & {
    transform: scale(1.12) rotate(-6deg);
  }
`;

export const PageIcon = styled(FileText)`
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${TreeItem}:hover & {
    transform: scale(1.12) rotate(-6deg);
  }
`;

export const ImagePageIcon = styled(ImageIcon)`
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${TreeItem}:hover & {
    transform: scale(1.12) rotate(-6deg);
  }
`;

export const PresentationPageIcon = styled(Presentation)`
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${TreeItem}:hover & {
    transform: scale(1.12) rotate(-6deg);
  }
`;

export const TreeTitle = styled.span`
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TreeTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export const SharedMetaRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const SharedStack = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SharedAvatar = styled.span<{ $idx: number }>`
  width: 14px;
  height: 14px;
  border-radius: 22%;
  border: 1px solid ${({ theme }) => theme.colors.bgSidebar};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.52rem;
  font-weight: 700;
  margin-left: ${({ $idx }) => ($idx === 0 ? '0' : '-4px')};
  overflow: hidden;
`;

export const SharedAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

export const SharedExtra = styled.span<{ $idx: number }>`
  height: 13px;
  min-width: 13px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 700;
  padding: 0 3px;
  margin-left: ${({ $idx }) => ($idx === 0 ? '0' : '-3px')};
`;

export const SharedLabel = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TreeRenameInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  padding: 2px 4px;
  background: ${({ theme }) => theme.colors.bgInput};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 4px;
`;

export const TreeActions = styled.span<{ $visible?: boolean; $showDropState?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: ${({ $visible, $showDropState }) => ($visible || $showDropState ? 1 : 0)};
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${TreeItem}:hover & {
    opacity: 1;
  }
`;

export const DropBadge = styled.span<{ $valid: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $valid }) => ($valid ? theme.colors.warning : theme.colors.error)};
  background: ${({ theme, $valid }) =>
    $valid
      ? theme.colors.warningBg
      : (theme.mode === 'dark' ? 'rgba(239,68,68,0.14)' : 'rgba(239,68,68,0.08)')};
  color: ${({ theme, $valid }) => ($valid ? theme.colors.warning : theme.colors.error)};
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;

  span {
    line-height: 1;
    white-space: nowrap;
  }
`;

export const WorkspaceActionBtn = styled.button<{ $danger?: boolean; $pin?: boolean }>`
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $danger, $pin }) => {
    if ($danger) return theme.colors.error;
    if ($pin) return theme.colors.textPrimary;
    return theme.colors.textSecondary;
  }};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme, $danger, $pin }) => {
      if ($danger) return theme.colors.error;
      if ($pin) return theme.colors.textPrimary;
      return theme.colors.textPrimary;
    }};
  }
`;

export const TeamWorkspaceWrap = styled.div`
  padding: 8px 0 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TeamSelectBtn = styled.button`
  width: 100%;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  font-weight: 500;
  min-height: 40px;
  padding: 10px 12px;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  }

  &:focus {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  }

  svg {
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)'};
    flex-shrink: 0;
  }
`;

export const AgentQuickActionWrap = styled.div`
  padding: 4px 0 10px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const AgentNewChatBtn = styled.button`
  flex: 1;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  text-align: left;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

export const AgentSearchBtn = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ $active, theme }) => $active ? theme.colors.textPrimary : theme.colors.textMuted};
  background: ${({ $active, theme }) => $active ? theme.colors.bgHover : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const SearchInputWrap = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  margin-bottom: 4px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

export const SearchClearBtn = styled.button`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textMuted};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const TeamHint = styled.div`
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const TeamSharedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TeamSharedPinnedDivider = styled.div`
  height: 1px;
  margin: 2px 0 4px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

export const TeamSharedItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

export const TeamSharedMainBtn = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 10px 10px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sidebar.historyGap};
  text-align: left;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 9px 8px;
  }
`;

export const TeamSharedActions = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${TeamSharedItem}:hover &,
  ${TeamSharedItem}:focus-within & {
    opacity: 1;
  }
`;

export const TeamSharedAvatars = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const TeamSharedAvatar = styled.span<{ $idx: number }>`
  width: 20px;
  height: 20px;
  border-radius: 22%;
  border: 1px solid ${({ theme }) => theme.colors.bgSidebar};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.58rem;
  font-weight: 700;
  margin-left: ${({ $idx }) => ($idx === 0 ? '0' : '-6px')};
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${TeamSharedItem}:hover & {
    transform: scale(1.12) rotate(-6deg);
  }
`;

export const TeamSharedAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

export const TeamSharedExtra = styled.span<{ $idx: number }>`
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.bgSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  font-weight: 700;
  padding: 0 3px;
  margin-left: ${({ $idx }) => ($idx === 0 ? '0' : '-3px')};
`;

export const TeamSharedTitle = styled.span`
  min-width: 0;
  flex: 1;
  font-size: ${({ theme }) => theme.sidebar.historyFontSize};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RecentWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
`;

export const RecentVirtuosoWrap = styled.div`
  width: 100%;
`;

export const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  padding: 0 6px 2px;
`;

export const LoadMoreTrigger = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  min-height: 1px;
  color: ${({ theme }) => theme.colors.textMuted};

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const GroupTitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 12px 10px 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.625rem;
    padding: 10px 8px 4px;
  }
`;

export const EmptyHint = styled.div`
  padding: 8px 10px 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
`;
